import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { withProps, withStateHandlers, compose, withHandlers } from 'recompose'
import getActiveVehicle, { getActiveVehicleName, getIsActiveVehicleTrailer } from 'selectors/getActiveVehicle'
import { handleAddNewVehicle } from 'actions/addVehicle'
import { showStoreLocatorModal } from 'actions/storeLocator'
import { googleAnalyticProductClicks } from 'actions/googleAnalytic'
import { showFindLowerPriceModal } from 'components/FindLowerPriceModal/actions'
import { addToProductCompare } from '../../actions'
import { TIRE_RESULT_PRODUCT, MY_FAVORITES_PAGE } from 'constant'
import { checkIsCompatible } from 'helpers/compatibility'
import {
  QTY_DEFAULT_VALUE,
  PRICE_SET_MAX,
  PRICE_SET_MIN,
  PROMOTIONS_MIN_ITEMS
} from '../../constants'
import { isCompareTrayFull, getIsAllCompatible, getOeItemIdsList } from '../../selectors'
import { getGTMData, getReviewsLink, getPromoAndSpecsMenu, productFlagIdCheck } from '../../helpers'
import { getProductAttributes } from './helpers'
import ProductBlock from './ProductBlock'
import { getLocationPathname } from 'components/Login/selectors'
import { getElectricVehicleSpecifications } from 'components/common/ProductFlag/helpers'
import { INVENTORY_STATUS_LOADED } from 'components/common/UpdatingBlock/constants.js'
import { getUserData } from 'components/Appointment/selectors'

const getCompatibleStatus = tire => tire.compatibilityState

const withProductProps = withProps(({
  isTrailer,
  index,
  product,
  product: {
    attributes: {
      frontTire: [frontTire],
      rearTire: [rearTire],
      promotions
    } = {}
  } = {}
}) => {
  const { skuId, tireDetailUrl, rearSkuId, staggeredSkuId, brandName } = getProductAttributes(product)

  const productSkuId = staggeredSkuId || skuId
  const expanderMenu = getPromoAndSpecsMenu(promotions)
  return {
    productSkuId,
    brandName,
    quantityInputId: `${TIRE_RESULT_PRODUCT}-${productSkuId}-${index}`,
    reviewsLink: getReviewsLink(tireDetailUrl),
    compatibilityState: getCompatibleStatus(rearTire || frontTire),
    isCompatible: isTrailer || (rearTire && checkIsCompatible(rearTire.compatibilityState)) ||
      (frontTire && checkIsCompatible(frontTire.compatibilityState)),
    defaultValue: QTY_DEFAULT_VALUE,
    staggeredSkuId,
    rearSkuId,
    skuId,
    expanderMenu
  }
})

export const getListSource = (url) => {
  let list = ''
  if (url.startsWith('/tires/by-vehicle')) {
    list = 'Search Results'
  }
  if (url.startsWith('/tires/sku')) {
    list = 'Tire Details'
  }
  if (url === MY_FAVORITES_PAGE) {
    list = 'Favorite'
  }
  return { list }
}

const withProductClickHandler = withHandlers({
  handleProductClick: ({ googleAnalyticProductClicks, product, index, currentUrl }) => e => {
    const source = getListSource(currentUrl)

    googleAnalyticProductClicks({
      data: getGTMData({ records: [product], position: index, source }), ...source
    })
  }
})

const mapStateToProps = (state, { compareTrayItems, productSkuId }) => {
  const addedToCompare = compareTrayItems.some(({ skuId }) => productSkuId === skuId)
  const oeItems = getOeItemIdsList(state)
  const { isPilotStore } = getUserData(state)

  return {
    myStore: state.myStore,
    activeVehicle: getActiveVehicle(state),
    vehicleName: getActiveVehicleName(state),
    isTrailer: getIsActiveVehicleTrailer(state),
    isAllCompatible: getIsAllCompatible(state),
    compareDisabled: isCompareTrayFull(state) || addedToCompare,
    compareBtnText: `Add${addedToCompare ? 'ed' : ''} to compare`,
    currentUrl: getLocationPathname(state),
    isOeItem: productFlagIdCheck(oeItems, productSkuId),
    inventoryStatuses: state.updatingBlocks[INVENTORY_STATUS_LOADED],
    isPilotStore
  }
}

const mapDispatchToProps = (dispatch, { product }) => {
  const {
    brandLogoUrl,
    imageUrl,
    skuId,
    tireDetailUrl,
    tireLineName,
    staggeredSkuId
  } = getProductAttributes(product)

  return {
    googleAnalyticProductClicks: bindActionCreators(googleAnalyticProductClicks, dispatch),
    handleAddNewVehicle: bindActionCreators(handleAddNewVehicle, dispatch),
    showStoreLocatorModal: bindActionCreators(showStoreLocatorModal, dispatch),

    handleFindLowerPriceClick() {
      dispatch(showFindLowerPriceModal(skuId))
    },

    handleCompareTrayAdd() {
      dispatch(addToProductCompare({
        brandLogoUrl,
        imageUrl,
        skuId: staggeredSkuId || skuId,
        tireDetailUrl,
        tireLineName,
        isStaggered: Boolean(staggeredSkuId)
      }))
    }
  }
}

export const getDefaultCountFront = (frontTire, rearTire) => {
  const { quantity } = frontTire || {}
  if (quantity) {
    return quantity
  }
  return (frontTire && rearTire) ? PRICE_SET_MIN : PRICE_SET_MAX
}

export const getDefaultRearFront = (frontTire, rearTire) => {
  const { quantity } = rearTire || {}
  if (quantity) {
    return quantity
  }
  return (frontTire && rearTire) ? PRICE_SET_MIN : PRICE_SET_MAX
}

const withHandleQtyChange = withStateHandlers(
  (
    ({
      product: {
        attributes: {
          frontTire: [frontTire],
          rearTire: [rearTire],
          promotions = []
        } = {}
      } = {}
    }) => ({
      countFront: getDefaultCountFront(frontTire, rearTire),
      countRear: getDefaultRearFront(frontTire, rearTire),
      promotions,
      promotionsPreviewItems: promotions.slice(0, PROMOTIONS_MIN_ITEMS),
      promotionsRestItems: promotions.slice(PROMOTIONS_MIN_ITEMS)
    })
  ),
  {
    handleFrontQuantityChange: () => value => ({
      countFront: value,
      quantityUpdated: true
    }),
    handleRearQuantityChange: () => value => ({
      countRear: value,
      quantityUpdated: true
    })
  }
)

const withDisableAddToQuote = withProps(({
  countFront,
  countRear,
  isCompatible,
  rearSkuId
}) => {
  const disableByQty = rearSkuId ? !countFront && !countRear : !countFront
  return {
    disableAddToQuote: !isCompatible || disableByQty
  }
})

const withEvFlagProp = withProps(({
  product: {
    attributes: {
      frontTire: [frontTire]
    } = {}
  } = {}
}) => {
  const { specification } = frontTire
  const electricVehicleSpecifications = getElectricVehicleSpecifications(specification)

  return {
    isEvItem: Boolean(electricVehicleSpecifications.length),
    itemEVType: electricVehicleSpecifications.length ? electricVehicleSpecifications[0].specValue : null
  }
})

export default compose(
  withProductProps,
  connect(mapStateToProps, mapDispatchToProps),
  withEvFlagProp,
  withProductClickHandler,
  withHandleQtyChange,
  withDisableAddToQuote
)(ProductBlock)
